import { z } from 'zod';

import {
  VIOLATION_BEHAVIOR_RULES,
  ViolationBehaviorRuleSchema,
} from '../violation-behavior-rule/violation-behavior-rule';
import { countsAgainstMember, type ViolationStatus } from '../violation-status/violation-status';

/**
 * What became of a behavioural flag — the seven statuses folded into the four
 * outcomes a reviewer reads a member's pattern by.
 *
 * Disjoint and exhaustive: every status lands in exactly one bucket, so the
 * four sum to the number of flags and nothing is counted twice. `enforced`
 * follows `countsAgainstMember` — an appealed or upheld consequence is still
 * standing against the member; only an overturn takes it off.
 */
export const VIOLATION_BEHAVIOR_OUTCOMES = [
  'pending',
  'enforced',
  'dismissed',
  'overturned',
] as const;

export type ViolationBehaviorOutcome = (typeof VIOLATION_BEHAVIOR_OUTCOMES)[number];

export function behaviorOutcomeOf(status: ViolationStatus): ViolationBehaviorOutcome {
  if (countsAgainstMember(status)) return 'enforced';
  if (status === 'dismissed' || status === 'overturned') return status;
  return 'pending';
}

/**
 * One rule's history against one member, as a reviewer opens a behavioural case.
 *
 * `lastDetectedAt` and the buckets are one fact: a rule with no flags has no
 * last detection, and a rule with a last detection was flagged at least once.
 * A row that says otherwise was assembled wrongly, and the schema says so
 * rather than letting a client render "last flagged in June" over four zeros.
 */
export const ViolationBehaviorHistoryEntrySchema = z
  .object({
    rule: ViolationBehaviorRuleSchema,
    pending: z.number().int().min(0),
    enforced: z.number().int().min(0),
    dismissed: z.number().int().min(0),
    overturned: z.number().int().min(0),
    lastDetectedAt: z.iso.datetime().nullable(),
  })
  .strict()
  .refine(
    (entry) =>
      (entry.lastDetectedAt === null) ===
      (entry.pending + entry.enforced + entry.dismissed + entry.overturned === 0),
    { error: 'lastDetectedAt is null exactly when nothing was counted', path: ['lastDetectedAt'] },
  );

export type ViolationBehaviorHistoryEntry = z.infer<typeof ViolationBehaviorHistoryEntrySchema>;

/**
 * Every declared rule, once, in declaration order — a rule the member never
 * tripped is a zero row, not a missing one. A reviewer scanning several cases
 * finds each rule in the same place every time, and "never" is information.
 */
export const ViolationBehaviorHistorySchema = z
  .array(ViolationBehaviorHistoryEntrySchema)
  .length(VIOLATION_BEHAVIOR_RULES.length)
  .refine(
    (entries) => entries.every((entry, index) => entry.rule === VIOLATION_BEHAVIOR_RULES[index]),
    {
      error: 'history lists every behavioural rule once, in declaration order',
    },
  );

export type ViolationBehaviorHistory = z.infer<typeof ViolationBehaviorHistorySchema>;
